import { CreatePaymentMethodDto, UpdatePaymentMethodDto, CreateWithdrawalDto } from "../types/payment";
import { HttpService } from "../modules/shared/services/http.service";

const headers = { "Content-Type": "application/json", "x-custom-lang": "en" } as const;

class PaymentApiService {

  // Payment Methods
  async getPaymentMethods(page: number = 1, limit: number = 10, filters?: {
    type?: string;
    status?: string;
    isDefault?: boolean;
  }) {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(filters?.type && { type: filters.type }),
      ...(filters?.status && { status: filters.status }),
      ...(filters?.isDefault !== undefined && { isDefault: filters.isDefault.toString() }),
    });
    
    const response = await HttpService.get(`api/v1/payment-methods?${queryParams}`, true, { "x-custom-lang": "en" });
    return response;
  }

  async createPaymentMethod(data: CreatePaymentMethodDto) {
    const response = await HttpService.post("api/v1/payment-methods", data, headers, true);
    return response;
  }

  async createBankAccountPaymentMethod(data: CreatePaymentMethodDto) {
    const response = await HttpService.post("api/v1/payment-methods/bank-account", data, headers, true);
    return response;
  }

  async createSetupIntent() {
    const response = await HttpService.get("api/v1/payment-methods/setup-intent", true, { "x-custom-lang": "en" });
    return response;
  }

  async updatePaymentMethod(id: string, data: UpdatePaymentMethodDto) {
    const response = await HttpService.patch(`api/v1/payment-methods/${id}`, data, headers, true);
    return response;
  }

  async deletePaymentMethod(id: string) {
    const response = await HttpService.deleteRecord(`api/v1/payment-methods/${id}`, { "x-custom-lang": "en" }, true);
    return response;
  }

  async setDefaultPaymentMethod(id: string) {
    const response = await HttpService.patch(`api/v1/payment-methods/${id}/set-default`, {}, headers, true);
    return response;
  }

  async getMyPaymentMethods() {
    const response = await HttpService.get("api/v1/payment-methods/my", true, { "x-custom-lang": "en" });
    return response;
  }

  async getDefaultPaymentMethod() {
    const response = await HttpService.get("api/v1/payment-methods/default", true, { "x-custom-lang": "en" });
    return response;
  }

  async getPaymentMethodById(id: string) {
    const response = await HttpService.get(`api/v1/payment-methods/${id}`, true, { "x-custom-lang": "en" });
    return response;
  }

  async createPayPalPaymentMethod(paypalEmail: string) {
    const response = await HttpService.post("api/v1/payment-methods/paypal", { paypalEmail }, headers, true);
    return response;
  }

  async verifyPayPalEmail(email: string) {
    const response = await HttpService.post("api/v1/payment-methods/verify-paypal-email", { email }, headers, true);
    return response;
  }

  async updatePayPalEmail(id: string, paypalEmail: string) {
    const response = await HttpService.patch(`api/v1/payment-methods/${id}`, { paypalEmail }, headers, true);
    return response;
  }


  // Withdrawals
  async getWithdrawals(page: number = 1, limit: number = 10, filters?: {
    status?: string;
    method?: string;
    withdrawalMethod?: string;
    startDate?: string;
    endDate?: string;
  }, sortOptions?: { orderBy: string; order: string }[]) {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(filters?.status && { status: filters.status }),
      ...(filters?.method && { method: filters.method }),
      ...(filters?.withdrawalMethod && { withdrawalMethod: filters.withdrawalMethod }),
      ...(filters?.startDate && { startDate: filters.startDate }),
      ...(filters?.endDate && { endDate: filters.endDate }),
    });

    // Add sort parameters
    if (sortOptions && sortOptions.length > 0) {
      queryParams.set("sort", JSON.stringify(sortOptions));
    }
    
    const response = await HttpService.get(`api/v1/withdrawals?${queryParams}`, true, { "x-custom-lang": "en" });
    return response;
  }

  // New Stripe Connect Status Check
  async checkStripeConnectStatus() {
    const response = await HttpService.get("api/v1/withdrawals/stripe-connect-status", true, { "x-custom-lang": "en" });
    return response;
  }

  // New Admin Balance Check
  async checkAdminBalance() {
    const response = await HttpService.get("api/v1/withdrawals/admin-balance", true, { "x-custom-lang": "en" });
    return response;
  }

  async createWithdrawal(data: CreateWithdrawalDto) {
    const response = await HttpService.post("api/v1/withdrawals", data, headers, true);
    return response;
  }

  async cancelWithdrawal(id: string) {
    const response = await HttpService.patch(`api/v1/withdrawals/${id}/cancel`, {}, headers, true);
    return response;
  }

  async deleteWithdrawal(id: string) {
    const response = await HttpService.deleteRecord(`api/v1/withdrawals/${id}`, { "x-custom-lang": "en" }, true);
    return response;
  }

  async getMyWithdrawals() {
    const response = await HttpService.get("api/v1/withdrawals/my", true, { "x-custom-lang": "en" });
    return response;
  }

  async getWithdrawalById(id: string) {
    const response = await HttpService.get(`api/v1/withdrawals/${id}`, true, { "x-custom-lang": "en" });
    return response;
  }

  async updateWithdrawal(id: string, data: any) {
    const response = await HttpService.patch(`api/v1/withdrawals/${id}`, data, headers, true);
    return response;
  }

  async markWithdrawalAsProcessing(id: string) {
    const response = await HttpService.patch(`api/v1/withdrawals/${id}/mark-processing`, {}, headers, true);
    return response;
  }

  async markWithdrawalAsCompleted(id: string) {
    const response = await HttpService.patch(`api/v1/withdrawals/${id}/mark-completed`, {}, headers, true);
    return response;
  }

  // Withdrawal Requests (New PayPal Integration)
  async getWithdrawalRequests(page: number = 1, limit: number = 10) {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    const response = await HttpService.get(`api/v1/withdrawal-requests?${queryParams}`, true, { "x-custom-lang": "en" });
    return response;
  }

  async createWithdrawalRequest(data: {
    amount: number;
    currency?: string;
    withdrawalMethod: "stripe" | "paypal" | "bank_account";
    paypalEmail?: string;
    metadata?: Record<string, any>;
  }) {
    const response = await HttpService.post("api/v1/withdrawal-requests", data, headers, true);
    return response;
  }

  async getWithdrawalRequestById(id: string) {
    const response = await HttpService.get(`api/v1/withdrawal-requests/${id}`, true, { "x-custom-lang": "en" });
    return response;
  }

  // Withdrawal History with Stats
  async getWithdrawalHistory(page: number = 1, limit: number = 10, filters?: {
    status?: number;
    withdrawalMethod?: string;
    startDate?: string;
    endDate?: string;
  }) {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(filters?.status && { status: filters.status.toString() }),
      ...(filters?.withdrawalMethod && { withdrawalMethod: filters.withdrawalMethod }),
      ...(filters?.startDate && { startDate: filters.startDate }),
      ...(filters?.endDate && { endDate: filters.endDate }),
    });
    
    const response = await HttpService.get(`api/v1/withdrawals/history/stats?${queryParams}`, true, { "x-custom-lang": "en" });
    return response;
  }
}

// Export the payment API service
export const apiService = new PaymentApiService();
